import { getAccessToken } from './auth/authApi';
import { apiClient } from '.';

// 검색 API 관련 타입 정의
export interface LectureSearchItem {
  lecture_info: {
    id: number;
    title: string;
    caption: string;
    thumbnail_url: string;
    tags: string[];
  };
  lecturer_info: {
    id: number;
    name: string;
    introduction: string;
    image_url: string;
  };
}

export interface LecturerSearchItem {
  id: number;
  name: string;
  birth: string | null;
  phone: string;
  email: string;
  identity: string;
  role: string;
  introduction: string;
  image_url: string;
  id_card_file_url: string | null;
  university: string | null;
  medicine: string | null;
  license: string | null;
  approved: boolean;
}

export interface PostSearchItem {
  id: number;
  title: string;
  category: string;
  lectureId: number | null;
  lectureTitle: string | null;
  lecturerId: number | null;
  lecturerName: string | null;
  studyCategoryName: string | null;
  pinned: boolean;
  exposed: boolean;
  writerName: string;
  writerProfileImageUrl: string | null;
  postDate: string;
  htmlcontent: string;
}

export interface SearchApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T[];
}

export interface SearchResults {
  lectures: LectureSearchItem[];
  lecturers: LecturerSearchItem[];
  posts: PostSearchItem[];
  archives: any[]; // 아직 구현 전이므로 임시 타입
}

// API 기본 URL
const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://campus-dev.duckdns.org';

// API 요청 헤더 생성
const getHeaders = () => {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// 강의 검색
export const searchLectures = async (keyword: string, limit?: number): Promise<LectureSearchItem[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/api/search/lectures`);
    url.searchParams.append('keyword', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<LectureSearchItem>>(
      url.pathname + url.search,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 제한이 있으면 해당 개수만큼만 반환
    return limit ? result.data.slice(0, limit) : result.data;
  } catch (error) {
    console.error('강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강사 검색 (커피챗)
export const searchLecturers = async (keyword: string, limit?: number): Promise<LecturerSearchItem[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/api/search/lecturers`);
    url.searchParams.append('keyword', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<LecturerSearchItem>>(
      url.pathname + url.search,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 제한이 있으면 해당 개수만큼만 반환
    return limit ? result.data.slice(0, limit) : result.data;
  } catch (error) {
    console.error('강사 검색 중 오류 발생:', error);
    throw error;
  }
};

// 게시글 검색 (커뮤니티)
export const searchPosts = async (keyword: string, limit?: number): Promise<PostSearchItem[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/api/search/posts`);
    url.searchParams.append('keyword', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      url.pathname + url.search,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 제한이 있으면 해당 개수만큼만 반환
    return limit ? result.data.slice(0, limit) : result.data;
  } catch (error) {
    console.error('게시글 검색 중 오류 발생:', error);
    throw error;
  }
};

// 아카이브 검색 (더미 데이터)
export const searchArchives = async (keyword: string, limit?: number): Promise<any[]> => {
  // 아직 구현 전이므로 더미 데이터 반환
  const dummyArchives = [
    {
      id: 1,
      title: `${keyword} 관련 아카이브 게시글 1`,
      author: '관리자',
      date: '2025년 7월 27일 12:00'
    },
    {
      id: 2,
      title: `${keyword} 관련 아카이브 게시글 2`,
      author: '관리자',
      date: '2025년 7월 26일 15:30'
    }
  ];

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(limit ? dummyArchives.slice(0, limit) : dummyArchives);
    }, 500); // 실제 API 호출과 비슷한 지연 시간
  });
};

// 강의별 Q&A 검색
export const searchLectureQnA = async (lectureId: number, keyword: string): Promise<PostSearchItem[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/api/search/posts/lecture/${lectureId}`);
    url.searchParams.append('keyword', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      url.pathname + url.search,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('강의 Q&A 검색 중 오류 발생:', error);
    throw error;
  }
};

// 카테고리별 강의 검색 (ClassCategory용)
export const searchLecturesByCategory = async (categoryId: number, keyword: string): Promise<LectureSearchItem[]> => {
  try {
    console.log('카테고리별 강의 검색 API 호출:', { categoryId, keyword });
    
    const { data: result } = await apiClient.get<SearchApiResponse<LectureSearchItem>>(
      `/api/search/lectures/${categoryId}?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('카테고리별 강의 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('카테고리별 강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 전체 강의 검색 (ClassCategory용)
export const searchAllLectures = async (keyword: string): Promise<LectureSearchItem[]> => {
  try {
    console.log('전체 강의 검색 API 호출:', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<LectureSearchItem>>(
      `/api/search/lectures?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('전체 강의 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('전체 강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 스터디 카테고리 검색
export const searchStudyPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    console.log('스터디 검색 API 호출:', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/5?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('스터디 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('스터디 검색 중 오류 발생:', error);
    throw error;
  }
};

// 커뮤니티 전체 게시글 검색
export const searchCommunityPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    console.log('커뮤니티 검색 API 호출:', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('커뮤니티 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('커뮤니티 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강의 질문 카테고리 검색
export const searchLectureQuestionPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    console.log('강의 질문 검색 API 호출:', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/2?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('강의 질문 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('강의 질문 검색 중 오류 발생:', error);
    throw error;
  }
};

// 정보공유 게시글 검색
export const searchInfoSharingPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    console.log('정보공유 검색 API 호출:', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/4?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('정보공유 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('정보공유 검색 중 오류 발생:', error);
    throw error;
  }
};

// 공지사항 게시글 검색
export const searchNoticePosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    console.log('공지사항 검색 API 호출:', keyword);
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/3?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    console.log('공지사항 검색 API 응답:', result);
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    console.error('공지사항 검색 중 오류 발생:', error);
    throw error;
  }
};

export interface LecturerSearchResponse {
  code: number;
  status: string;
  message: string;
  data: LecturerSearchItem[];
}



// 통합 검색 (병렬 호출)
export const searchAll = async (keyword: string): Promise<SearchResults> => {
  try {
    const [lectures, lecturers, posts, archives] = await Promise.all([
      searchLectures(keyword, 4),
      searchLecturers(keyword, 4),
      searchPosts(keyword, 2),
      searchArchives(keyword, 2)
    ]);

    return {
      lectures,
      lecturers,
      posts,
      archives
    };
  } catch (error) {
    console.error('통합 검색 중 오류 발생:', error);
    throw error;
  }
};
